import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, ArrowLeft, Shield, Send, CheckCircle, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }),
};

function StarRating({ value, onChange, label }) {
  return (
    <div>
      <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">{label}</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} type="button" onClick={() => onChange(s)} className="p-0.5" data-testid={`star-${label.toLowerCase().replace(/\s+/g, "-")}-${s}`}>
            <Star size={20} className={`transition-colors ${s <= value ? "text-gold fill-gold" : "text-white/20 hover:text-gold/50"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ItemDetailPage() {
  const { category, itemId } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: "", reviewer_email: "", overall_rating: 0, ticketing_rating: 0,
    ambiance_rating: 0, bathroom_rating: 0, food_drink_rating: 0, liked: "", improve: "", consent_marketing: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/recommendations/${category}/${itemId}`).then((r) => setItem(r.data)).catch(() => toast.error("Item not found")).finally(() => setLoading(false));
  }, [category, itemId]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.reviewer_name || !reviewForm.reviewer_email || !reviewForm.overall_rating) {
      toast.error("Please provide your name, email, and overall rating."); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { ...reviewForm, item_id: itemId });
      setSubmitted(true);
      toast.success("Review submitted. Thank you for your feedback!");
      // Refresh item to show new review
      const res = await axios.get(`${API}/recommendations/${category}/${itemId}`);
      setItem(res.data);
    } catch { toast.error("Failed to submit review."); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold font-body animate-pulse">Loading...</div></div>;
  if (!item) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><p className="text-white/40 font-body">Item not found.</p></div>;

  const reviews = item.reviews || [];

  return (
    <div data-testid="item-detail-page" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <Link to="/recommendations" className="inline-flex items-center gap-2 text-white/40 hover:text-gold text-sm font-body mb-8 transition-colors" data-testid="back-to-recs">
          <ArrowLeft size={16} /> Back to Recommendations
        </Link>

        {/* Item Header */}
        <motion.div initial="hidden" animate="visible">
          {item.image_url && (
            <motion.div variants={fadeUp} custom={0} className="aspect-[21/9] overflow-hidden mb-8">
              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
            </motion.div>
          )}
          <motion.div variants={fadeUp} custom={1} className="flex items-center gap-3 mb-3">
            <span className="text-gold text-xs font-body font-medium uppercase tracking-wider">#{item.rank}</span>
            {item.avg_rating > 0 && (
              <div className="flex items-center gap-1">
                <Star size={14} className="text-gold fill-gold" />
                <span className="text-gold text-sm font-body">{item.avg_rating}</span>
                <span className="text-white/30 text-sm font-body">({item.review_count} reviews)</span>
              </div>
            )}
          </motion.div>
          <motion.h1 variants={fadeUp} custom={2} className="font-heading text-4xl sm:text-5xl font-light text-white mb-4" data-testid="item-title">{item.name}</motion.h1>
          {item.location && (
            <motion.div variants={fadeUp} custom={3} className="flex items-center gap-2 text-white/40 text-sm font-body mb-4">
              <MapPin size={14} /> {item.location}
            </motion.div>
          )}
          {item.description && <motion.p variants={fadeUp} custom={4} className="text-white/60 font-body font-light text-base leading-relaxed mb-6">{item.description}</motion.p>}
          {item.tags?.length > 0 && (
            <motion.div variants={fadeUp} custom={5} className="flex flex-wrap gap-2 mb-10">
              {item.tags.map((t) => <span key={t} className="px-3 py-1 text-xs border border-white/10 text-white/50 font-body">{t}</span>)}
            </motion.div>
          )}
        </motion.div>

        {/* Reviews Section */}
        <div className="border-t border-white/10 pt-10">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl text-white">Guest Reviews ({reviews.length})</h2>
            {!submitted && (
              <button data-testid="write-review-btn" onClick={() => setShowReviewForm(!showReviewForm)} className="flex items-center gap-2 bg-gold/15 text-gold px-5 py-2 text-xs font-body hover:bg-gold/25 transition-colors">
                <Star size={14} /> Write a Review
              </button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && !submitted && (
            <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmitReview} className="glass-card p-8 mb-10 space-y-6" data-testid="review-form">
              <h3 className="text-white font-body text-sm font-medium">Share Your Experience</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Your Name *</label>
                  <Input data-testid="review-name" value={reviewForm.reviewer_name} onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Email *</label>
                  <Input data-testid="review-email" type="email" value={reviewForm.reviewer_email} onChange={(e) => setReviewForm({ ...reviewForm, reviewer_email: e.target.value })} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
                </div>
              </div>

              {/* Star Ratings */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                <StarRating label="Overall *" value={reviewForm.overall_rating} onChange={(v) => setReviewForm({ ...reviewForm, overall_rating: v })} />
                <StarRating label="Ticketing" value={reviewForm.ticketing_rating} onChange={(v) => setReviewForm({ ...reviewForm, ticketing_rating: v })} />
                <StarRating label="Ambiance" value={reviewForm.ambiance_rating} onChange={(v) => setReviewForm({ ...reviewForm, ambiance_rating: v })} />
                <StarRating label="Bathroom" value={reviewForm.bathroom_rating} onChange={(v) => setReviewForm({ ...reviewForm, bathroom_rating: v })} />
                <StarRating label="Food & Drink" value={reviewForm.food_drink_rating} onChange={(v) => setReviewForm({ ...reviewForm, food_drink_rating: v })} />
              </div>

              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">What did you enjoy?</label>
                <Textarea data-testid="review-liked" value={reviewForm.liked} onChange={(e) => setReviewForm({ ...reviewForm, liked: e.target.value })} placeholder="Tell us what you loved about the experience..." rows={3} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm text-sm resize-none" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">What could be improved?</label>
                <Textarea data-testid="review-improve" value={reviewForm.improve} onChange={(e) => setReviewForm({ ...reviewForm, improve: e.target.value })} placeholder="Constructive feedback helps everyone improve..." rows={3} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm text-sm resize-none" />
              </div>

              {/* Data Privacy */}
              <div className="p-4 bg-white/[0.02] border border-white/5">
                <div className="flex items-start gap-3">
                  <Shield size={16} className="text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white/50 text-xs font-body leading-relaxed">
                      Your review helps improve experiences for our community. We collect this feedback solely to enhance our recommendations.
                      <strong className="text-white/70"> Your personal information will never be sold to third parties without your explicit consent.</strong>
                    </p>
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                      <input type="checkbox" checked={reviewForm.consent_marketing} onChange={(e) => setReviewForm({ ...reviewForm, consent_marketing: e.target.checked })} className="accent-gold" data-testid="review-consent" />
                      <span className="text-white/40 text-xs font-body">I'd like to receive curated recommendations and exclusive event invitations (optional)</span>
                    </label>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={submitting} data-testid="submit-review" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors disabled:opacity-50">
                {submitting ? "Submitting..." : "Submit Review"} <Send size={16} />
              </button>
            </motion.form>
          )}

          {submitted && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-8 mb-10 text-center">
              <CheckCircle size={36} className="text-gold mx-auto mb-3" />
              <h3 className="text-white font-heading text-xl mb-2">Thank You!</h3>
              <p className="text-white/50 font-body text-sm">Your review has been submitted and will help our community discover the best experiences.</p>
            </motion.div>
          )}

          {/* Existing Reviews */}
          {reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/30 font-body text-sm">No reviews yet. Be the first to share your experience.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviews.map((r, i) => (
                <motion.div key={r.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="glass-card p-6" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-body text-sm font-medium">{r.reviewer_name}</p>
                      <p className="text-white/30 text-xs font-body">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : ""}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: r.overall_rating }).map((_, j) => <Star key={j} size={12} className="text-gold fill-gold" />)}
                      {Array.from({ length: 5 - r.overall_rating }).map((_, j) => <Star key={j} size={12} className="text-white/15" />)}
                    </div>
                  </div>
                  {/* Category ratings */}
                  <div className="flex flex-wrap gap-4 mb-3">
                    {r.ticketing_rating && <span className="text-white/30 text-[10px] font-body">Ticketing: {r.ticketing_rating}/5</span>}
                    {r.ambiance_rating && <span className="text-white/30 text-[10px] font-body">Ambiance: {r.ambiance_rating}/5</span>}
                    {r.bathroom_rating && <span className="text-white/30 text-[10px] font-body">Bathroom: {r.bathroom_rating}/5</span>}
                    {r.food_drink_rating && <span className="text-white/30 text-[10px] font-body">Food & Drink: {r.food_drink_rating}/5</span>}
                  </div>
                  {r.liked && (
                    <div className="mb-2">
                      <span className="text-gold text-[10px] font-body uppercase tracking-wider">Liked:</span>
                      <p className="text-white/60 font-body text-xs leading-relaxed mt-1">{r.liked}</p>
                    </div>
                  )}
                  {r.improve && (
                    <div>
                      <span className="text-white/40 text-[10px] font-body uppercase tracking-wider">Could improve:</span>
                      <p className="text-white/50 font-body text-xs leading-relaxed mt-1">{r.improve}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
